import React from 'react';
import { motion } from 'framer-motion';
import { Network, Plus, Minus, Info } from 'lucide-react';

/**
 * MeshNodeSelector component
 * Allows users to select the number of mesh nodes for their wireless network
 * 
 * @param {Object} props - Component props
 * @param {number} props.nodeCount - Current node count
 * @param {Function} props.onNodeCountChange - Function to call when node count changes
 * @param {number} props.minNodes - Minimum number of nodes (default: 1)
 * @param {number} props.maxNodes - Maximum number of nodes (default: 6)
 * @param {Object} props.coverageData - Coverage data for the current node count
 * @returns {JSX.Element} MeshNodeSelector component
 */
const MeshNodeSelector = ({
  nodeCount,
  onNodeCountChange,
  minNodes = 1,
  maxNodes = 6,
  coverageData,
  recommendation
}) => {
  // Handle increment node count
  const incrementNodeCount = () => {
    if (nodeCount < maxNodes) {
      onNodeCountChange(nodeCount + 1);
    }
  };
  
  // Handle decrement node count
  const decrementNodeCount = () => {
    if (nodeCount > minNodes) {
      onNodeCountChange(nodeCount - 1);
    }
  };
  
  // Format coverage percentage
  const formatPercentage = (percentage) => {
    return `${Math.round(percentage)}%`;
  };
  
  // Format area
  const formatArea = (area) => {
    return `${Math.round(area)}m²`;
  };
  
  return (
    <div className="mb-6">
      <div className="flex items-center mb-3">
        <Network className="h-5 w-5 text-purple-600 mr-2" />
        <h3 className="text-lg font-medium text-gray-900">Mesh Node Count</h3>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-600">Select the number of mesh nodes for your network:</p>
            <p className="text-sm text-gray-500 mt-1">More nodes provide better coverage but increase cost</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <motion.button
              className={`p-2 rounded-full ${
                nodeCount > minNodes
                  ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              onClick={decrementNodeCount}
              disabled={nodeCount <= minNodes}
              whileHover={nodeCount > minNodes ? { scale: 1.1 } : {}}
              whileTap={nodeCount > minNodes ? { scale: 0.9 } : {}}
            >
              <Minus className="h-5 w-5" />
            </motion.button>
            
            <div className="w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center text-xl font-bold">
              {nodeCount}
            </div>
            
            <motion.button
              className={`p-2 rounded-full ${
                nodeCount < maxNodes
                  ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              onClick={incrementNodeCount}
              disabled={nodeCount >= maxNodes}
              whileHover={nodeCount < maxNodes ? { scale: 1.1 } : {}}
              whileTap={nodeCount < maxNodes ? { scale: 0.9 } : {}}
            >
              <Plus className="h-5 w-5" />
            </motion.button>
          </div>
        </div>
        
        {/* Recommended Nodes for 100% Coverage */}
        <div className="mt-4 mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Recommended Nodes for 100% Coverage</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Basic Coverage */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <div className="flex justify-between items-center">
                <h5 className="text-sm font-medium text-green-700">Basic Coverage</h5>
                <div className="bg-green-600 text-white text-sm font-bold rounded-full h-6 w-6 flex items-center justify-center">
                  {recommendation?.meshSystem.coverageLevels.basic.units || 3}
                </div>
              </div>
              <p className="text-xs text-green-600 mt-1">
                Sufficient for basic internet browsing and email
              </p>
            </div>
            
            {/* Medium Coverage */}
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <div className="flex justify-between items-center">
                <h5 className="text-sm font-medium text-purple-700">Medium Coverage</h5>
                <div className="bg-purple-600 text-white text-sm font-bold rounded-full h-6 w-6 flex items-center justify-center">
                  {recommendation?.meshSystem.coverageLevels.medium.units || 4}
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-1">
                Good for HD streaming and video calls
              </p>
            </div>
            
            {/* High-Speed Coverage */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex justify-between items-center">
                <h5 className="text-sm font-medium text-blue-700">High-Speed Coverage</h5>
                <div className="bg-blue-600 text-white text-sm font-bold rounded-full h-6 w-6 flex items-center justify-center">
                  {recommendation?.meshSystem.coverageLevels.high.units || 5}
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Ideal for 4K streaming, gaming, and multiple devices
              </p>
            </div>
          </div>
        </div>
        
        {coverageData && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Estimated Coverage with {nodeCount} {nodeCount === 1 ? 'Node' : 'Nodes'}</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* High-Speed Coverage */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <h5 className="text-sm font-medium text-blue-700">High-Speed Coverage</h5>
                <div className="mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-600 font-medium">{formatPercentage(coverageData.coveragePercentages.high)}</span>
                    <span className="text-gray-500">{formatArea(coverageData.coverage.highSpeed)} covered</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${coverageData.coveragePercentages.high}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              {/* Medium-Speed Coverage */}
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <h5 className="text-sm font-medium text-purple-700">Medium-Speed Coverage</h5>
                <div className="mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-purple-600 font-medium">{formatPercentage(coverageData.coveragePercentages.medium)}</span>
                    <span className="text-gray-500">{formatArea(coverageData.coverage.mediumSpeed)} covered</span>
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${coverageData.coveragePercentages.medium}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              {/* Basic Coverage */}
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <h5 className="text-sm font-medium text-green-700">Basic Coverage</h5>
                <div className="mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 font-medium">{formatPercentage(coverageData.coveragePercentages.low)}</span>
                    <span className="text-gray-500">{formatArea(coverageData.coverage.lowSpeed)} covered</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${coverageData.coveragePercentages.low}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="mt-4 flex items-start bg-blue-50 p-3 rounded-lg border border-blue-100">
          <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p>The number of mesh nodes directly impacts your wireless coverage.</p>
            <p className="mt-1">For optimal coverage in most homes, we recommend at least 2-3 nodes.</p>
            <p className="mt-1">Each additional node increases coverage in areas with weak signal.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeshNodeSelector;